import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { getMyObservations, getMyAssignedObservations } from '../../services/observationAPI';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'; // Import recharts components

// Define colors for the charts (Updated for Unsafe Act/Condition)
const COLORS = ['#FF9800', '#EF4444']; // Amber for Unsafe Act, Red for Unsafe Condition

const EmployeeDashboard = () => {
    const [stats, setStats] = useState({
        totalObservations: 0,
        // Updated state names for observation types
        unsafeActObservations: 0,
        unsafeConditionObservations: 0,
        pendingActions: 0,
        completedActions: 0,
    });
    const [loading, setLoading] = useState(true);
    const [recentActions, setRecentActions] = useState([]);
    // Data for Unsafe Act/Condition Pie Chart
    const [observationTypeData, setObservationTypeData] = useState([]);
    // Data for Top Unsafe Observations list (renamed from topAtRiskObservations)
    const [topUnsafeObservations, setTopUnsafeObservations] = useState([]);
    const [areaData, setAreaData] = useState([]); // Data for Area Wise Bar Graph
    // Data for simple Heat Map (Unsafe Condition counts per area)
    const [heatmapData, setHeatmapData] = useState({});

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Fetch observations and actions using the provided functions
                const [observationsRes, actionsRes] = await Promise.all([
                    getMyObservations(),
                    getMyAssignedObservations()
                ]);

                // Ensure data is an array and filter out any non-object items
                // Also filter out observations where area or area.name is missing/invalid for charts/heatmap
                const observations = (observationsRes.observations || [])
                    .filter(item => item && typeof item === 'object');

                const actions = (actionsRes.observations || [])
                    .filter(item => item && typeof item === 'object');

                // Process observation data
                // Calculate counts for new observation types
                const unsafeActObservations = observations.filter((obs) => obs.type === 'unsafe act').length;
                const unsafeConditionObservations = observations.filter((obs) => obs.type === 'unsafe condition').length;

                // Prepare data for Unsafe Act/Condition Pie Chart
                const pieChartData = [
                    { name: 'Unsafe Act', value: unsafeActObservations },
                    { name: 'Unsafe Condition', value: unsafeConditionObservations },
                ].filter(item => item.value > 0); // Only include if count is greater than 0

                setObservationTypeData(pieChartData);

                // Prepare data for Top Unsafe Observations (limit to top 5, for example)
                // Filtering for both unsafe act and unsafe condition
                const unsafeList = observations.filter(obs => obs.type === 'unsafe act' || obs.type === 'unsafe condition');
                // If observations had a 'severity' or 'priority' field, we could sort here.
                // For now, just taking the first few unsafe observations.
                setTopUnsafeObservations(unsafeList.slice(0, 5));


                // Prepare data for Area Wise Observations Bar Graph (counting all observations by area)
                // Access obs.area.name
                const areaCounts = observations.reduce((acc, obs) => {
                    // Use optional chaining and provide a fallback
                    const areaName = obs.area?.name || 'Unknown Area';
                    acc[areaName] = (acc[areaName] || 0) + 1;
                    return acc;
                }, {});
                const areaBarData = Object.keys(areaCounts).map(areaName => ({
                    area: areaName, // Use the extracted area name
                    count: areaCounts[areaName],
                }));
                setAreaData(areaBarData);

                // Prepare data for simple Heat Map (Unsafe Condition counts per area)
                // Access obs.area.name
                const unsafeConditionAreaCounts = observations
                    .filter(obs => obs.type === 'unsafe condition') // Filter specifically for unsafe condition
                    .reduce((acc, obs) => {
                        // Use optional chaining and provide a fallback
                        const areaName = obs.area?.name || 'Unknown Area';
                        acc[areaName] = (acc[areaName] || 0) + 1;
                        return acc;
                    }, {});
                setHeatmapData(unsafeConditionAreaCounts);


                // Process action data. The key is now 'observations' in the API response
                const pendingActions = actions.filter((action) =>
                    action.status === 'waiting' || action.status === 'in-progress' || action.status === 'submitted'
                ).length; // Adjusted pending states
                const completedActions = actions.filter((action) => action.status === 'completed').length;

                setStats({
                    totalObservations: observations.length,
                    // Updated state values
                    unsafeActObservations,
                    unsafeConditionObservations,
                    pendingActions,
                    completedActions,
                });

                // Get recent actions (limit to 5)
                setRecentActions(actions.slice(0, 5));

            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                // Set stats and data to default/empty on error
                setStats({
                    totalObservations: 0,
                    unsafeActObservations: 0,
                    unsafeConditionObservations: 0,
                    pendingActions: 0,
                    completedActions: 0,
                });
                setObservationTypeData([]);
                setTopUnsafeObservations([]);
                setAreaData([]);
                setHeatmapData({});
                setRecentActions([]);

            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []); // Empty dependency array means this effect runs once on mount

    // Helper function to get heatmap color based on count
    const getHeatmapColor = (count) => {
        if (count === 0) return 'bg-green-200';
        if (count > 0 && count <= 2) return 'bg-yellow-300';
        if (count > 2 && count <= 5) return 'bg-orange-400';
        if (count > 5) return 'bg-red-500';
        return 'bg-gray-200'; // Default
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-4"> {/* Added padding */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Employee Dashboard</h1>
                <p className="text-gray-600">Overview of your safety observations and actions.</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card bg-white rounded-lg shadow-md p-4"> {/* Added card styling */}
                    <div className="flex justify-between items-start p-2">
                        <div>
                            <p className="text-sm text-gray-500">My Observations</p>
                            <p className="text-2xl font-semibold">{stats.totalObservations}</p>
                        </div>
                        <div className="rounded-full bg-blue-100 p-2">
                            <ClipboardList className="h-5 w-5 text-blue-600" />
                        </div>
                    </div>
                </div>

                {/* Stat cards for Unsafe Act and Unsafe Condition */}
                <div className="card bg-white rounded-lg shadow-md p-4"> {/* Added card styling */}
                    <div className="flex justify-between items-start p-2">
                        <div>
                            <p className="text-sm text-gray-500">Unsafe Act Observations</p>
                            <p className="text-2xl font-semibold">{stats.unsafeActObservations}</p>
                        </div>
                        <div className="rounded-full bg-amber-100 p-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600" /> {/* Using AlertTriangle for unsafe */}
                        </div>
                    </div>
                </div>

                <div className="card bg-white rounded-lg shadow-md p-4"> {/* Added card styling */}
                    <div className="flex justify-between items-start p-2">
                        <div>
                            <p className="text-sm text-gray-500">Unsafe Condition Observations</p>
                            <p className="text-2xl font-semibold">{stats.unsafeConditionObservations}</p>
                        </div>
                        <div className="rounded-full bg-red-100 p-2">
                            <AlertTriangle className="h-5 w-5 text-red-600" /> {/* Using AlertTriangle for unsafe */}
                        </div>
                    </div>
                </div>


                <div className="card bg-white rounded-lg shadow-md p-4"> {/* Added card styling */}
                    <div className="flex justify-between items-start p-2">
                        <div>
                            <p className="text-sm text-gray-500">Pending Actions</p>
                            <p className="text-2xl font-semibold">{stats.pendingActions}</p>
                        </div>
                        <div className="rounded-full bg-amber-100 p-2">
                            <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                    <div className="space-y-3">
                        <Link
                            to="/employee/submit-observation"
                            className="btn btn-primary w-full flex items-center justify-center bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200" // Added Tailwind button styles
                        >
                            <ClipboardList className="h-5 w-5 mr-2" />
                            Submit New Observation
                        </Link>
                        <Link
                            to="/employee/my-actions"
                             className="btn btn-secondary w-full flex items-center justify-center bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition duration-200" // Added Tailwind button styles
                        >
                            <CheckCircle className="h-5 w-5 mr-2" />
                            View My Actions
                        </Link>
                    </div>
                </div>

                <div className=" bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-lg font-semibold mb-4">My Recent Actions</h2> {/* Changed title for clarity */}
                    {recentActions.length > 0 ? (
                        <div className="space-y-3">
                            {recentActions.map((action, index) => {
                                // Explicitly check if action is a valid object before rendering
                                if (!action || typeof action !== 'object') {
                                    console.warn('Skipping invalid action data in list:', action);
                                    return null; // Skip rendering this item
                                }
                                return (
                                    <div
                                        key={action._id || `action-${index}`} // Use a fallback key
                                        className={`p-3 rounded-lg border-l-4 ${
                                            action.status === 'waiting'
                                                ? 'border-amber-500 bg-amber-50'
                                                : action.status === 'in-progress'
                                                ? 'border-purple-500 bg-purple-50'
                                                : action.status === 'submitted'
                                                ? 'border-blue-500 bg-blue-50'
                                                : action.status === 'completed'
                                                ? 'border-green-500 bg-green-50'
                                                : 'border-gray-500 bg-gray-50' // Added a default case
                                        }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                {/* Ensure description and status are treated as strings */}
                                                <p className="font-medium text-sm">
                                                    {typeof action.description === 'string' ? action.description.substring(0, 50) + (action.description.length > 50 ? '...' : '') : 'No description'}
                                                </p>
                                                <div className="mt-1 flex items-center text-xs text-gray-500">
                                                    <span>Created At: {action?.createdAt ? new Date(action.createdAt).toLocaleDateString() : 'N/A'}</span>
                                                    <span className="mx-1">•</span>
                                                    <span
                                                        className={`status-badge text-xs font-semibold px-2.5 py-0.5 rounded-full ${ // Added Tailwind status badge styles
                                                            typeof action.status === 'string'
                                                                ? action.status === 'waiting'
                                                                    ? 'bg-amber-100 text-amber-800'
                                                                    : action.status === 'in-progress'
                                                                    ? 'bg-purple-100 text-purple-800'
                                                                    : action.status === 'submitted'
                                                                    ? 'bg-blue-100 text-blue-800'
                                                                    : action.status === 'completed'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-gray-100 text-gray-800'
                                                                : 'bg-gray-100 text-gray-800' // Handle non-string status
                                                        }`}
                                                    >
                                                         {typeof action.status === 'string' ? action.status.replace('-', ' ') : 'Unknown Status'} {/* Display status, replace hyphen */}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <Link
                                to="/employee/my-actions"
                                className="block text-sm text-blue-600 hover:text-blue-800 text-center mt-2"
                            >
                                View all actions &rarr;
                            </Link>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No active actions at the moment.</p>
                    )}
                </div>
            </div>

            {/* Charts and Lists Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Unsafe Act/Condition Ratio Chart (Updated Title and Data) */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-lg font-semibold mb-4">Unsafe Act vs Unsafe Condition Observations</h2> {/* Updated Title */}
                    {observationTypeData.length > 0 ? 
                    (<ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={observationTypeData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {observationTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>)
                     : 
                    ( <p className="text-gray-500 text-center py-4">No unsafe observation data available for the chart.</p>)
                    }
                </div>

                {/* Top Unsafe Observations (Updated Title and Filtering) */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-lg font-semibold mb-4">Top Unsafe Observations</h2> {/* Updated Title */}
                    {topUnsafeObservations.length > 0 ? (
                        <ul className="divide-y divide-gray-200">
                            {topUnsafeObservations.map((obs, index) => {
                                // Explicitly check if obs is a valid object before rendering
                                if (!obs || typeof obs !== 'object') {
                                    console.warn('Skipping invalid observation data in list:', obs);
                                    return null; // Skip rendering this item
                                }
                                return (
                                    <li key={obs._id || `obs-${index}`} className="py-3"> {/* Use a fallback key */}
                                        <div className="flex items-center space-x-3">
                                            {/* Icon based on observation type */}
                                            <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${obs.type === 'unsafe act' ? 'text-amber-500' : 'text-red-500'}`} />
                                            <div className="flex-1 min-w-0">
                                                {/* Ensure description and access area.name */}
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {typeof obs.description === 'string' ? obs.description.substring(0, 50) + (obs.description.length > 50 ? '...' : '') : 'No description'}
                                                </p>
                                                <p className="text-sm text-gray-500 truncate">
                                                    Type: {obs.type === 'unsafe act' ? 'Unsafe Act' : 'Unsafe Condition'} • Area: {obs.area?.name || 'Unknown Area'}
                                                </p>
                                            </div>
                                            {/* Add a link or button to view details if available */}
                                            {/* <Link to={`/observations/${obs._id}`} className="text-sm text-blue-600 hover:text-blue-800">View</Link> */}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                         <p className="text-gray-500 text-center py-4">No unsafe observations found.</p> 
                    )}
                </div>

                 

                {/* Area Wise Observations Bar Graph (Counts all observations by area) */}
                <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2"> {/* Span across two columns on large screens */}
                    <h2 className="text-lg font-semibold mb-4">Observations by Area</h2> {/* Title remains the same as it counts all */}
                     {areaData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={areaData}
                                margin={{
                                    top: 5, right: 30, left: 20, bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="area" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" fill="#8884d8" name="Total Observations" />
                            </BarChart>
                        </ResponsiveContainer>
                     ) : (
                         <p className="text-gray-500 text-center py-4">No area data available for the chart.</p>
                     )}
                </div>

                {/* Simple Heat Map (Unsafe Condition Count by Area) - Updated Title and Filtering */}
                <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2"> {/* Span across two columns on large screens */}
                    <h2 className="text-lg font-semibold mb-4">Unsafe Condition Heatmap by Area</h2> {/* Updated Title */}
                    {/* Added a check for Object.keys(heatmapData).length > 0 || areaData.length > 0 to render the container even if only areas with 0 unsafe conditions exist */}
                    {Object.keys(heatmapData).length > 0 || areaData.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {/* Map areas that have unsafe conditions */}
                            {Object.keys(heatmapData).map(areaName => (
                                <div
                                    key={areaName}
                                    className={`p-4 rounded-lg text-center ${getHeatmapColor(heatmapData[areaName])}`}
                                >
                                    <p className="font-semibold text-sm">{areaName}</p>
                                    <p className="text-xl font-bold mt-1">{heatmapData[areaName]}</p>
                                    <p className="text-xs">Unsafe Condition</p>
                                </div>
                            ))}
                             {/* Add areas with 0 unsafe condition observations for completeness */}
                             {/* Filter areas from areaData that are not in heatmapData (meaning they have 0 unsafe conditions) */}
                             {areaData.filter(item => !heatmapData.hasOwnProperty(item.area)).map((item, index) => (
                                  <div
                                      key={item.area || `area-${index}`} // Use item.area as the key
                                      className={`p-4 rounded-lg text-center ${getHeatmapColor(0)}`}
                                  >
                                       <p className="font-semibold text-sm">{item.area}</p> {/* Display item.area */}
                                       <p className="text-xl font-bold mt-1">0</p> {/* 0 is a number */}
                                       <p className="text-xs">Unsafe Condition</p> {/* Updated label */}
                                   </div>
                             ))}
                        </div>
                    ) : (
                         <p className="text-gray-500 text-center py-4">No unsafe condition data available for the heatmap.</p> 
                    )}
                </div>


            </div>


           

            {/* Safety Tips */}
            <div className="card bg-blue-50 border border-blue-100 rounded-lg shadow-md p-4"> {/* Added card styling */}
                <div className="flex items-start">
                    <div className="rounded-full bg-blue-100 p-2 mr-4">
                        <AlertTriangle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-medium text-blue-800">Safety Tip of the Day</h3>
                        <p className="text-blue-700 mt-1">
                            Always report unsafe conditions immediately. Your observation today could prevent an
                            incident tomorrow.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
